import React from 'react'
import moment from 'moment'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import Button from '../button'
import { Order } from '../../../core/orders'
import { getOrderTableContent, strings } from './orders-content'
import styles from '../../styles/modules/table.module.scss'
import ordersTableStyles from '../../styles/modules/orders/orders-table.module.scss'

interface Props {
  orders: Order[]
  onClickCancelButton?: (order: Order) => void
  onClickKioskPurchaserImageButton: (imageUrl: string, alt: string) => void
}

const OrdersTable: React.FC<Props> = props => {
  const tableHeaders = getOrderTableContent()

  const renderHeaderText = (header: string) => {
    if (header === 'Togoバッグ') {
      return (
        <>
          Togo
          <br />
          バッグ
        </>
      )
    }
    if (header === 'シュガー/シロップ') {
      return (
        <>
          シュガー
          <br />
          シロップ
        </>
      )
    }
    return header
  }

  const renderCondimentValue = (value?: boolean) => {
    if (value === true) {
      return strings.condimentSelected
    }
    return strings.condimentNotSelected
  }

  const renderLockerBoxValue = (order: Order) => {
    if (order.coffeeLog == null || order.coffeeLog.lockerBox == null) {
      return '未割当'
    }
    return order.coffeeLog.lockerBox.index
  }

  const renderCoffeeValue = (order: Order) =>
    `ID: ${order.item.id} ${order.item.name}`

  const renderFoodValue = (order: Order) => {
    if (order.food) {
      return `ID: ${order.food.id} ${order.food.name}`
    }
    return strings.foodUnknown
  }

  const renderTime = (value?: string | null) =>
    value ? moment(value).format('HH:mm:ss') : ''

  const renderTimeSlot = (value: string) => moment(value).format('HH:mm')

  return (
    <>
      <h3 className={styles.title}>{strings.ordersTableTitle}</h3>
      <div
        className={classnames({
          [styles.wrapper]: true,
          [styles.zebra]: true,
          [styles.stocker]: true,
        })}
      >
        <div>
          キャンセル:単発→返金/Limited→杯数カウントが戻る/Unlimited→特に無し
        </div>
        <br />
        <div>表示件数: {props.orders.length}件</div>
        <br />
        <table className={styles.container}>
          <tbody>
            <tr className={styles.row}>
              {tableHeaders.map(item => (
                <th
                  key={item}
                  className={
                    item === 'Togoバッグ' || item === 'シュガー/シロップ'
                      ? `${styles.cell} ${ordersTableStyles.headerCell}`
                      : styles.cell
                  }
                >
                  {renderHeaderText(item)}
                </th>
              ))}
            </tr>
            {props.orders &&
              props.orders.map(item => (
                <tr
                  key={item.id}
                  className={classnames({
                    [styles.row]: true,
                    [ordersTableStyles.row]: true,
                    [ordersTableStyles.hasTroubleRow]: item.isEmergencyRelated,
                  })}
                >
                  <td className={styles.cell}>
                    {moment(item.orderedAt).format('YYYY/MM/DD/ HH:mm:ss')}
                  </td>
                  <td className={styles.cell}>{item.hashid}</td>
                  <td className={styles.cell}>{item.provisionStatus}</td>
                  <td className={styles.cell}>{renderLockerBoxValue(item)}</td>
                  <td className={styles.cell}>{renderCoffeeValue(item)}</td>
                  <td className={styles.cell}>{renderFoodValue(item)}</td>
                  <td className={styles.cell} style={{ textAlign: 'center' }}>
                    {renderCondimentValue(item.paperBag)}
                  </td>
                  <td className={styles.cell} style={{ textAlign: 'center' }}>
                    {renderCondimentValue(item.potionMilk)}
                  </td>
                  <td className={styles.cell} style={{ textAlign: 'center' }}>
                    {renderCondimentValue(item.sugarOrSyrup)}
                  </td>
                  <td className={styles.cell}>
                    {renderTime(item.coffeeLog?.makingStartedAt)}
                  </td>
                  <td className={styles.cell}>
                    {renderTime(item.coffeeLog?.makingDoneAt)}
                  </td>
                  <td className={styles.cell}>
                    {renderTime(item.coffeeLog?.servedAt)}
                  </td>
                  <td className={styles.cell}>
                    {renderTime(item.coffeeLog?.doorClosedAt)}
                  </td>
                  <td className={styles.cell}>{item.id}</td>
                  <td className={styles.cell}>{item.status}</td>
                  <td className={styles.cell}>
                    {item.status === 'canceled' ||
                    item.status === 'pre_canceled'
                      ? strings.canceled
                      : ''}
                  </td>
                  <td className={styles.cell}>
                    {item.isEmergencyRelated ? strings.hasTrouble : ''}
                  </td>
                  <td className={styles.cell}>{item.paymentMethod}</td>
                  <td className={styles.cell}>{item.paidAmount}</td>
                  <td className={styles.cell}>{item.salo01PaymentId ?? ''}</td>
                  <td className={styles.cell}>
                    {props.onClickCancelButton &&
                    item.status !== 'canceled' &&
                    item.status !== 'pre_canceled' ? (
                      <Button
                        title={strings.cancel}
                        onClick={() => {
                          props.onClickCancelButton?.(item)
                        }}
                        className={ordersTableStyles.container}
                      />
                    ) : null}
                  </td>
                  <td className={styles.cell}>
                    {item.kioskID ? 'KIOSK' : 'Mobile'}
                  </td>
                  <td className={styles.cell}>
                    {renderTimeSlot(item.timeSlotFrom)}
                  </td>
                  <td className={styles.cell}>
                    {item.coffeeLog ? item.coffeeLog.id : '未調理'}
                  </td>
                  <td className={styles.cell}>
                    <Link
                      to={{
                        pathname: `/users/${item.userID}`,
                      }}
                    >
                      {item.userID}
                    </Link>
                  </td>
                  <td className={styles.cell}>{item.kioskID}</td>
                  <td className={styles.cell}>
                    <Link
                      to={{
                        pathname: `/stations/${item.station.id}`,
                      }}
                    >
                      {`ID: ${item.station.id} ${item.station.name}`}
                    </Link>
                  </td>
                  <td className={styles.cell}>{item.pointDiscountAmount}</td>
                  <td className={styles.cell}>{item.campaignDiscountAmount}</td>
                  <td className={styles.cell}>
                    {item.oneMoreCoffeeDiscountAmount}
                  </td>
                  <td className={styles.cell}>
                    {item.kioskPurchaserImageURL !== '' ? (
                      <Button
                        title={strings.kioskPurchaseImage}
                        onClick={() => {
                          props.onClickKioskPurchaserImageButton(
                            item.kioskPurchaserImageURL,
                            'KIOSK購入者画像'
                          )
                        }}
                        className={ordersTableStyles.container}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default OrdersTable
